/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// 💬 Anomaly Investigation SQL
/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// Drill-in queries used by `AnomalyInvestigationDrawer` to populate the
/// "what drove this spike" + "who was involved" sections.
///
/// Both queries are scoped to a single (warehouse, day) tuple — the
/// dimensions of an anomaly row — so the result sets stay small (top 10
/// queries, top 5 users) and the SQL is cheap even on large accounts.
///
/// Day filter is built with TO_DATE so we don't accidentally match a
/// timestamp boundary; warehouse name is double-quoted-literal-escaped
/// because Snowflake warehouse names allow most printable characters.

import { quoteString } from './userQueries'
import { CREDIT_PRICE_USD } from './costQueries'

/**
 * Top heaviest queries (by EXECUTION_TIME) on `warehouse` during the
 * UTC calendar day `day` (YYYY-MM-DD). Returns enough metadata for the
 * drawer to build human-readable rows: who ran it, what it ran, how
 * long, what it scanned, what size warehouse was active, and a
 * 300-char preview of the query text. Rows are sorted heaviest-first
 * so the drawer can highlight whether one or two queries dominated
 * the day's spend.
 */
export function topQueriesForWarehouseDaySql(warehouse: string, day: string): string {
  const wh = quoteString(warehouse)
  const d = quoteString(day)
  return `
SELECT
  QUERY_ID                                                           AS "queryId",
  USER_NAME                                                          AS "userName",
  ROLE_NAME                                                          AS "roleName",
  WAREHOUSE_SIZE                                                     AS "warehouseSize",
  TO_CHAR(START_TIME, 'YYYY-MM-DD"T"HH24:MI:SS"Z"')                  AS "startTime",
  TO_CHAR(END_TIME,   'YYYY-MM-DD"T"HH24:MI:SS"Z"')                  AS "endTime",
  ROUND(EXECUTION_TIME / 1000.0, 1)                                  AS "execSeconds",
  COALESCE(BYTES_SCANNED, 0)                                         AS "bytesScanned",
  COALESCE(ROWS_PRODUCED, 0)                                         AS "rowsProduced",
  ROUND(COALESCE(CREDITS_USED_CLOUD_SERVICES, 0)
        * ${CREDIT_PRICE_USD}, 4)                                    AS "cloudServicesCost",
  EXECUTION_STATUS                                                   AS "executionStatus",
  QUERY_TYPE                                                         AS "queryType",
  LEFT(QUERY_TEXT, 400)                                              AS "queryPreview"
FROM SNOWFLAKE.ACCOUNT_USAGE.QUERY_HISTORY
WHERE WAREHOUSE_NAME = ${wh}
  AND DATE_TRUNC('DAY', START_TIME) = TO_DATE(${d})
  AND EXECUTION_TIME IS NOT NULL
ORDER BY EXECUTION_TIME DESC NULLS LAST
LIMIT 10
`
}

/**
 * Top users (by execution-time share) on `warehouse` during `day`.
 * Used by the drawer to attribute the spike to a few people without
 * needing to scroll the heaviest-queries table. We compute exec-time
 * share rather than absolute credits because per-query credit
 * attribution requires the warehouse-prorate trick (same as
 * byUserSql); the share is a reasonable proxy and avoids a second
 * heavyweight CTE for a section that only needs ranking, not totals.
 */
export function topUsersForWarehouseDaySql(warehouse: string, day: string): string {
  const wh = quoteString(warehouse)
  const d = quoteString(day)
  return `
WITH per_user AS (
  SELECT
    USER_NAME,
    COUNT(*)                                       AS query_count,
    SUM(EXECUTION_TIME)                            AS exec_ms,
    SUM(COALESCE(CREDITS_USED_CLOUD_SERVICES, 0))  AS cs_credits,
    MAX(START_TIME)                                AS last_query_at
  FROM SNOWFLAKE.ACCOUNT_USAGE.QUERY_HISTORY
  WHERE WAREHOUSE_NAME = ${wh}
    AND DATE_TRUNC('DAY', START_TIME) = TO_DATE(${d})
    AND USER_NAME IS NOT NULL
  GROUP BY USER_NAME
),
total AS (
  SELECT NULLIF(SUM(exec_ms), 0) AS total_exec_ms FROM per_user
)
SELECT
  pu.USER_NAME                                                                    AS "userName",
  pu.query_count                                                                  AS "queryCount",
  ROUND(pu.exec_ms / 1000.0, 1)                                                   AS "execSeconds",
  ROUND(COALESCE(pu.exec_ms / total.total_exec_ms, 0) * 100, 1)                   AS "execSharePct",
  ROUND(pu.cs_credits * ${CREDIT_PRICE_USD}, 2)                                   AS "cloudServicesCost",
  TO_CHAR(pu.last_query_at, 'YYYY-MM-DD"T"HH24:MI:SS"Z"')                         AS "lastQueryAt"
FROM per_user pu
CROSS JOIN total
ORDER BY pu.exec_ms DESC NULLS LAST
LIMIT 5
`
}
